import React, { useState } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  useMediaQuery,
  useTheme,
  Grid
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TimelineIcon from '@mui/icons-material/Timeline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useSnackbar } from 'notistack';
import { Link, useNavigate } from 'react-router-dom';
import ExportTradeHistoryModal from './ExportTradeHistoryModal';

const TradeHistory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  
  const [openExportModal, setOpenExportModal] = useState(false);
  
  const handleOpenModal = () => setOpenExportModal(true);
  const handleCloseModal = () => setOpenExportModal(false);

  const handlePnlHistoryClick = () => {
    navigate('/pnl-history/details');
  };

  const handleLedgerClick = () => {
    navigate('/ledger/details', {
      state: {
        filters: {
          startDate: new Date(),
          endDate: new Date(),
          ledgerType: 'BILL'
        }
      }
    });
  };

  return (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 3 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AssessmentIcon sx={{ 
            mr: 1, 
            color: theme.palette.primary.main,
            fontSize: 28
          }} />
          <Typography variant="h5" component="h1" sx={{ 
            fontWeight: 600,
            color: theme.palette.primary.main
          }}>
            Reports
          </Typography>
        </Box>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            onClick={handleOpenModal}
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: (theme) => `0 8px 16px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.1)'}`,
              },
              background: (theme) => theme.palette.mode === 'dark' 
                ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
                : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              borderRadius: 2,
              border: (theme) => theme.palette.mode === 'dark' 
                ? `1px solid ${theme.palette.divider}`
                : 'none',
              height: '100%'
            }}
          >
            <CardContent sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              p: 3
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                gap: 2
              }}>
                <HistoryIcon sx={{ 
                  fontSize: 40, 
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? theme.palette.primary.light 
                    : theme.palette.primary.main
                }} />
                <MailOutlineIcon sx={{ 
                  fontSize: 32, 
                  color: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.secondary.light
                    : theme.palette.secondary.main
                }} />
              </Box>
              <Typography 
                variant="h6" 
                component="h2" 
                align="center" 
                sx={{ 
                  fontWeight: 600,
                  color: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.text.primary
                }}
              >
                Trade History
              </Typography>
              <Typography 
                variant="body2" 
                align="center" 
                sx={{ 
                  mt: 1,
                  color: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.text.secondary
                    : theme.palette.text.secondary,
                  opacity: (theme) => theme.palette.mode === 'dark' ? 0.8 : 1
                }}
              >
                Download your trade history for a specific date range and segment
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card
            onClick={handlePnlHistoryClick}
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: (theme) => `0 8px 16px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.1)'}`,
              },
              background: (theme) => theme.palette.mode === 'dark' 
                ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[800]} 100%)`
                : 'linear-gradient(135deg, #f5f7fa 0%, #d4e4ff 100%)',
              borderRadius: 2,
              border: (theme) => theme.palette.mode === 'dark' 
                ? `1px solid ${theme.palette.divider}`
                : 'none',
              height: '100%'
            }}
          >
            <CardContent sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              p: 3
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                gap: 2
              }}>
                <TimelineIcon sx={{ 
                  fontSize: 40, 
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? theme.palette.success.light 
                    : theme.palette.success.main
                }} />
                <MonetizationOnIcon sx={{ 
                  fontSize: 32, 
                  color: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.success.light
                    : theme.palette.success.main
                }} />
              </Box>
              <Typography 
                variant="h6" 
                component="h2" 
                align="center" 
                sx={{ 
                  fontWeight: 600,
                  color: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.success.light
                    : theme.palette.text.primary
                }}
              >
                PnL History
              </Typography>
              <Typography 
                variant="body2" 
                align="center" 
                sx={{ 
                  mt: 1,
                  color: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.text.secondary
                    : theme.palette.text.secondary,
                  opacity: (theme) => theme.palette.mode === 'dark' ? 0.8 : 1
                }}
              >
                View and analyze your profit and loss history for performance insights
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card
            onClick={handleLedgerClick}
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: (theme) => `0 8px 16px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.1)'}`,
              },
              background: (theme) => theme.palette.mode === 'dark' 
                ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[700]} 100%)`
                : 'linear-gradient(135deg, #f5f7fa 0%, #e0e7ff 100%)',
              borderRadius: 2,
              border: (theme) => theme.palette.mode === 'dark' 
                ? `1px solid ${theme.palette.divider}`
                : 'none',
              height: '100%'
            }}
          >
            <CardContent sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              p: 3
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                gap: 2
              }}>
                <AccountBalanceIcon sx={{ 
                  fontSize: 40, 
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? theme.palette.info.light 
                    : theme.palette.info.main
                }} />
                <ReceiptLongIcon sx={{ 
                  fontSize: 32, 
                  color: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.info.light
                    : theme.palette.info.main
                }} />
              </Box>
              <Typography 
                variant="h6" 
                component="h2" 
                align="center" 
                sx={{ 
                  fontWeight: 600,
                  color: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.info.light
                    : theme.palette.text.primary
                }}
              >
                Ledger
              </Typography>
              <Typography 
                variant="body2" 
                align="center" 
                sx={{ 
                  mt: 1,
                  color: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.text.secondary
                    : theme.palette.text.secondary,
                  opacity: (theme) => theme.palette.mode === 'dark' ? 0.8 : 1
                }}
              >
                View your complete transaction history and account statements
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <ExportTradeHistoryModal
        open={openExportModal}
        onClose={handleCloseModal}
        defaultExchange="NFO"
        title="Download Trade History"
        showViewDetailsButton={true}
        onViewDetails={() => window.location.href = '/trade-history/details'}
      />
    </Box>
  );
};

export default TradeHistory; 